import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ChevronLeft, ChevronRight, TrendingUp, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import AppHeader from '../components/AppHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Calendar() {
  const [calendarData, setCalendarData] = useState({});
  const [currentDate, setCurrentDate] = useState(new Date());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCalendar();
  }, []);

  const fetchCalendar = async () => {
    try {
      const { data } = await axios.get(`${API}/letters/calendar`, { withCredentials: true });
      setCalendarData(data);
    } catch (error) {
      console.error('Error fetching calendar:', error);
    }
    setLoading(false);
  };

  const getMonthDays = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    const startPadding = firstDay.getDay();

    for (let i = 0; i < startPadding; i++) {
      days.push(null);
    }
    for (let day = 1; day <= lastDay.getDate(); day++) {
      days.push(new Date(year, month, day));
    }
    return days;
  };

  const formatDateKey = (date) => {
    return date.toISOString().split('T')[0];
  };

  const getIntensity = (count) => {
    if (count === 0) return 'bg-[#F5F1EB]';
    if (count === 1) return 'bg-[#B94A3E]/30';
    if (count === 2) return 'bg-[#B94A3E]/60';
    return 'bg-[#B94A3E]';
  };

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const days = getMonthDays(currentDate);
  const monthName = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  // Calculate streak
  const sortedDates = Object.keys(calendarData).sort().reverse();
  let currentStreak = 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  for (let i = 0; i < 365; i++) {
    const checkDate = new Date(today);
    checkDate.setDate(today.getDate() - i);
    const key = formatDateKey(checkDate);
    if (calendarData[key]) {
      currentStreak++;
    } else if (i > 0) {
      break;
    }
  }

  const totalLetters = sortedDates.reduce((sum, date) => sum + (calendarData[date]?.count || 0), 0);

  return (
    <div className="min-h-screen bg-[#F9F7F3]">
      <AppHeader />
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <div className="mb-8">
          <Link to="/dashboard">
            <Button variant="ghost" size="sm" className="mb-4" data-testid="back-to-dashboard-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />Back to Dashboard
            </Button>
          </Link>
          <h1 className="text-4xl font-light tracking-tight mb-2" style={{fontFamily: 'Spectral, serif'}}>Letter Calendar</h1>
          <p className="text-base text-[#737373]">Track your consistency in showing up for your loved one.</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white border border-[#E5E0D8] rounded-md p-6 shadow-[2px_2px_0px_rgba(26,26,26,0.08)]">
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp className="w-5 h-5 text-[#B94A3E]" />
              <span className="label-text">Current Streak</span>
            </div>
            <div className="text-4xl font-light" style={{fontFamily: 'Spectral, serif'}}>{currentStreak}</div>
            <p className="text-sm text-[#737373] mt-1">{currentStreak === 1 ? 'day' : 'days'} in a row</p>
          </div>
          <div className="bg-white border border-[#E5E0D8] rounded-md p-6 shadow-[2px_2px_0px_rgba(26,26,26,0.08)]">
            <div className="flex items-center gap-3 mb-2">
              <Mail className="w-5 h-5 text-[#B94A3E]" />
              <span className="label-text">Total Letters</span>
            </div>
            <div className="text-4xl font-light" style={{fontFamily: 'Spectral, serif'}}>{totalLetters}</div>
            <p className="text-sm text-[#737373] mt-1">letters written</p>
          </div>
          <div className="bg-white border border-[#E5E0D8] rounded-md p-6 shadow-[2px_2px_0px_rgba(26,26,26,0.08)]">
            <div className="flex items-center gap-3 mb-2">
              <Mail className="w-5 h-5 text-[#B94A3E]" />
              <span className="label-text">Active Days</span>
            </div>
            <div className="text-4xl font-light" style={{fontFamily: 'Spectral, serif'}}>{sortedDates.length}</div>
            <p className="text-sm text-[#737373] mt-1">days you wrote</p>
          </div>
        </div>

        <div className="bg-white border border-[#E5E0D8] rounded-md p-8 shadow-[2px_2px_0px_rgba(26,26,26,0.08)]">
          <div className="flex items-center justify-between mb-8">
            <Button variant="ghost" size="sm" onClick={prevMonth} data-testid="prev-month-btn">
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <h3 className="text-2xl font-light" style={{fontFamily: 'Spectral, serif'}}>{monthName}</h3>
            <Button variant="ghost" size="sm" onClick={nextMonth} data-testid="next-month-btn">
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>

          {loading ? (
            <p className="text-center text-[#737373] py-12">Loading calendar...</p>
          ) : (
            <>
              <div className="grid grid-cols-7 gap-2 mb-2">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                  <div key={day} className="text-center label-text py-2">{day}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-2">
                {days.map((day, idx) => {
                  if (!day) return <div key={idx} className="aspect-square" />;
                  const key = formatDateKey(day);
                  const dayData = calendarData[key];
                  const count = dayData?.count || 0;
                  const isToday = key === formatDateKey(new Date());
                  return (
                    <div
                      key={idx}
                      className={`aspect-square rounded-md ${getIntensity(count)} border ${isToday ? 'border-[#B94A3E] border-2' : 'border-[#E5E0D8]'} flex flex-col items-center justify-center transition-colors hover:opacity-80 cursor-pointer`}
                      data-testid={`calendar-day-${key}`}
                      title={count > 0 ? `${count} letter${count > 1 ? 's' : ''}` : 'No letters'}
                    >
                      <span className={`text-sm ${count > 2 ? 'text-white' : 'text-[#1A1A1A]'}`}>{day.getDate()}</span>
                      {count > 0 && (
                        <span className={`text-xs ${count > 2 ? 'text-white' : 'text-[#737373]'}`}>{count}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          <div className="mt-8 flex items-center justify-between text-xs text-[#737373]">
            <span>Less</span>
            <div className="flex gap-1">
              <div className="w-4 h-4 rounded bg-[#F5F1EB] border border-[#E5E0D8]" />
              <div className="w-4 h-4 rounded bg-[#B94A3E]/30" />
              <div className="w-4 h-4 rounded bg-[#B94A3E]/60" />
              <div className="w-4 h-4 rounded bg-[#B94A3E]" />
            </div>
            <span>More</span>
          </div>
        </div>

        <div className="mt-8 bg-[#F5EBE0] border border-[#B94A3E]/20 rounded-md p-6 text-center">
          <p className="text-lg" style={{fontFamily: 'Spectral, serif'}}>
            {currentStreak === 0 
              ? "Start your streak today. Write a letter."
              : `You've been showing up for ${currentStreak} ${currentStreak === 1 ? 'day' : 'days'}. Keep the connection alive.`
            }
          </p>
          <Link to="/letters">
            <Button className="mt-4 bg-[#B94A3E] hover:bg-[#9d3e33] text-white" data-testid="write-letter-cta-btn">
              Write Today's Letter
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
